import Typography from "@mui/material/Typography";
import React from "react";
import { type SetterOrUpdater, useRecoilState, useRecoilValue } from "recoil";
import CustomSelect from "../components/CustomSelect";
import {
	atEveryOptions,
	atOptionsNonAdmin,
	defaultHourOptions,
	DEFAULT_HOUR_OPTS_AT,
	DEFAULT_HOUR_OPTS_EVERY,
} from "../constants";
import {
	hourAtEveryState,
	hourRangeEndSchedulerState,
	hourRangeStartSchedulerState,
	hourState,
	isAdminState,
	localeState,
} from "../store";
import { getTimesOfTheDay } from "../utils";
import Grid2 from "@mui/material/Grid2";
import type { SelectOptions } from "../types";

const POSSIBLE_TIME_RANGES = getTimesOfTheDay();

export default function Hour() {
	const [hourAtEvery, setHourAtEvery] = useRecoilState(hourAtEveryState);
	const [startHour, setStartHour] = useRecoilState(
		hourRangeStartSchedulerState,
	);
	const [endHour, setEndHour] = useRecoilState(hourRangeEndSchedulerState);
	const [hour, setHour] = useRecoilState(hourState);
	const [hourOptions, setHourOptions] = React.useState(defaultHourOptions);

	const [possibleStartTimes, setPossibleStartTimes] =
		React.useState(POSSIBLE_TIME_RANGES);

	const [possibleEndTimes, setPossibleEndTimes] =
		React.useState(POSSIBLE_TIME_RANGES);

	React.useEffect(() => {
		const startIndex = possibleStartTimes.findIndex(
			(x) => x.value === startHour.value,
		);
		const limitedPossibleTimeRange = possibleEndTimes.map(
			(possibleEndTime, index) => ({
				...possibleEndTime,
				disabled: index <= startIndex,
			}),
		);
		setPossibleEndTimes(limitedPossibleTimeRange);
	}, [startHour]);

	React.useEffect(() => {
		const endIndex = possibleEndTimes.findIndex(
			(x) => x.value === endHour.value,
		);
		const limitedPossibleTimeRange = possibleStartTimes.map(
			(possibleStartTime, index) => ({
				...possibleStartTime,
				disabled: index >= endIndex,
			}),
		);
		setPossibleStartTimes(limitedPossibleTimeRange);
	}, [endHour]);

	const isAdmin = useRecoilValue(isAdminState);

	React.useEffect(() => {
		if (hourAtEvery.value === "every") {
			if (hour.length > 1) {
				setHour([hourOptions[1]]);
			} else if (hour[0].value === "0") {
				setHour([hourOptions[1]]);
			}
			setHourOptions(DEFAULT_HOUR_OPTS_EVERY);
		} else {
			setHourOptions(DEFAULT_HOUR_OPTS_AT);
		}
	}, [hourAtEvery, isAdmin]);

	React.useEffect(() => {
		if (!isAdmin && hour.length > 1) {
			setHour((prevHour) => [prevHour[0]]);
		}
	}, [isAdmin]);

	const resolvedLocale = useRecoilValue(localeState);

	return (
		<>
			<Grid2 container>
				<Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
					<CustomSelect
						single
						options={
							isAdmin
								? atEveryOptions(
										resolvedLocale.atOptionLabel,
										resolvedLocale.everyOptionLabel,
									)
								: atOptionsNonAdmin(
										resolvedLocale.atOptionLabel,
										resolvedLocale.everyOptionLabel,
									)
						}
						label={resolvedLocale.atEveryText}
						value={hourAtEvery}
						setValue={
							setHourAtEvery as SetterOrUpdater<SelectOptions | SelectOptions[]>
						}
						multiple={false}
						disableClearable
					/>
				</Grid2>

				<Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
					<CustomSelect
						options={hourOptions}
						label={resolvedLocale.hourLabel}
						value={hour}
						setValue={
							setHour as SetterOrUpdater<SelectOptions | SelectOptions[]>
						}
						single={hourAtEvery.value === "every" || !isAdmin}
						sort
						disableEmpty
						limitTags={3}
						disableClearable={hourAtEvery.value === "every" || hour.length < 2}
						disabled={!isAdmin && hourAtEvery.value === "every"}
					/>
				</Grid2>

				{hourAtEvery.value === "every" && (
					<>
						<Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
							<Typography>{resolvedLocale.betweenText}</Typography>
							<CustomSelect
								single
								options={possibleStartTimes}
								label={""}
								value={startHour}
								setValue={
									setStartHour as SetterOrUpdater<
										SelectOptions | SelectOptions[]
									>
								}
								multiple={false}
								disableClearable
								disabled={!isAdmin}
							/>
						</Grid2>

						<Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
							<Typography>{resolvedLocale.andText}</Typography>
							<CustomSelect
								single
								options={possibleEndTimes}
								label={""}
								value={endHour}
								setValue={
									setEndHour as SetterOrUpdater<SelectOptions | SelectOptions[]>
								}
								multiple={false}
								disableClearable
								disabled={!isAdmin}
							/>
						</Grid2>
					</>
				)}
			</Grid2>
		</>
	);
}
